import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X } from "lucide-react";
import { CHECKBOX_CLS } from "@/lib/calendar";

export const NEMT_AIDS = [["rolling_walker", "Rolling Walker"], ["cane", "Cane"], ["crutches", "Crutches"], ["wheelchair", "Wheelchair"]];
export const NEMT_NEEDS = [["step_stool", "Step Stool"], ["entry_exit_help", "Help In/Out"], ["blind", "Visually Impaired"], ["deaf", "Hearing Impaired"], ["oxygen", "Oxygen Tank"], ["service_animal", "Service Animal"]];
const PRIVATE_AIDS = [["none", "None"], ["walker", "Walker"], ["cane", "Cane"], ["other", "Other"]];
const PRIVATE_ACCESS = [["none", "None"], ["hearing_impaired", "Hearing impaired / Deaf"], ["visually_impaired", "Blind / Visually impaired"], ["other", "Other"]];
export const PAYMENTS = [["card", "Credit/Debit Card"], ["cashapp", "Cash App"], ["chime", "Chime"], ["applepay", "Apple Pay"], ["cash", "Cash"]];

export const Field = ({ label, children }) => <div className="space-y-1"><Label className="text-xs font-semibold">{label}</Label>{children}</div>;

export const Pick = ({ value, onChange, options, testId, placeholder }) => (
  <Select value={value ?? ""} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

const toggleIn = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

export const StopsEditor = ({ stops, setStops }) => (
  <div className="space-y-2 rounded-xl border border-orange-200 bg-orange-50 p-3" data-testid="phone-stops-editor">
    <Label className="text-xs font-semibold">Additional stops (in order)</Label>
    {stops.map((s, i) => (
      <div key={`stop-${i}`} className="flex gap-2">
        <Input placeholder={`Stop ${i + 1} address`} value={s.address} onChange={(e) => setStops(stops.map((x, j) => (j === i ? { ...x, address: e.target.value } : x)))} data-testid={`phone-stop-${i}-input`} />
        <Input type="number" min="0" className="w-24" placeholder="wait min" value={s.wait_minutes || ""} onChange={(e) => setStops(stops.map((x, j) => (j === i ? { ...x, wait: Number(e.target.value) > 0, wait_minutes: Number(e.target.value) || 0 } : x)))} data-testid={`phone-stop-${i}-wait`} />
        <Button type="button" variant="ghost" size="icon" onClick={() => setStops(stops.filter((_, j) => j !== i))}><X className="w-4 h-4" /></Button>
      </div>
    ))}
    <Button type="button" variant="outline" size="sm" onClick={() => setStops([...stops, { address: "", wait: false, wait_minutes: 0 }])} data-testid="phone-add-stop-btn">
      <Plus className="w-4 h-4 mr-1" />Add stop
    </Button>
  </div>
);

export const AssistanceFields = ({ f, set }) => {
  if (f.ride_type === "nemt") {
    return (
      <div className="space-y-2">
        <Label className="text-xs font-semibold">Mobility aids</Label>
        <div className="flex flex-wrap gap-3">
          {NEMT_AIDS.map(([v, l]) => (
            <label key={v} className="flex items-center gap-2 text-sm"><Checkbox className={CHECKBOX_CLS} checked={f.mobility_aids.includes(v)} onCheckedChange={() => set("mobility_aids", toggleIn(f.mobility_aids, v))} data-testid={`phone-aid-${v}`} />{l}</label>
          ))}
        </div>
        <Label className="text-xs font-semibold">Assistance needs</Label>
        <div className="flex flex-wrap gap-3">
          {NEMT_NEEDS.map(([v, l]) => (
            <label key={v} className="flex items-center gap-2 text-sm"><Checkbox className={CHECKBOX_CLS} checked={f.assistance_needs.includes(v)} onCheckedChange={() => set("assistance_needs", toggleIn(f.assistance_needs, v))} data-testid={`phone-need-${v}`} />{l}</label>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <Field label="Help getting in/out of vehicle?"><Pick value={f.needs_entry_assistance === null ? "" : String(f.needs_entry_assistance)} onChange={(v) => set("needs_entry_assistance", v === "true")} options={[["false", "No"], ["true", "Yes"]]} testId="phone-entry-assist" placeholder="Select" /></Field>
      <Field label="Service animal?"><Pick value={f.has_service_animal === null ? "" : String(f.has_service_animal)} onChange={(v) => set("has_service_animal", v === "true")} options={[["false", "No"], ["true", "Yes"]]} testId="phone-service-animal" placeholder="Select" /></Field>
      <Field label="Mobility aid"><Pick value={f.mobility_aid} onChange={(v) => set("mobility_aid", v)} options={PRIVATE_AIDS} testId="phone-mobility-aid" placeholder="Select" /></Field>
      {f.mobility_aid === "other" && <Field label="Describe mobility aid"><Input value={f.mobility_aid_other} onChange={(e) => set("mobility_aid_other", e.target.value)} /></Field>}
      <Field label="Accessibility need"><Pick value={f.accessibility_need} onChange={(v) => set("accessibility_need", v)} options={PRIVATE_ACCESS} testId="phone-accessibility" placeholder="Select" /></Field>
      {f.accessibility_need === "other" && <Field label="Describe accessibility need"><Input value={f.accessibility_other} onChange={(e) => set("accessibility_other", e.target.value)} /></Field>}
    </div>
  );
};
